import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Copy, Banknote, Wallet, Package, Calendar, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { formatJMD, todayStr, PAYMENT_LABEL } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const empty = {
  customer_name: "",
  phone: "",
  email: "",
  address: "",
  order_type: "purchase",
  product_id: "",
  quantity: 1,
  delivery_date: todayStr(),
  payment_method: "cod",
  notes: "",
};

export const OrderSection = ({ products, selected, reorder, bankDetails }) => {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    if (!reorder || products.length === 0) return;
    const item = reorder.items?.[0];
    const prod = item && products.find((p) => p.id === item.product_id && p.stock_status === "in_stock");
    setForm((f) => ({
      ...f,
      customer_name: reorder.customer_name || "",
      phone: reorder.phone || "",
      email: reorder.email || "",
      address: reorder.address || "",
      payment_method: reorder.payment_method || "cod",
      notes: reorder.notes || "",
      order_type: prod ? prod.type : f.order_type,
      product_id: prod ? prod.id : "",
      quantity: item?.quantity || 1,
    }));
  }, [reorder, products]);

  useEffect(() => {
    if (selected) {
      setForm((f) => ({ ...f, order_type: selected.type, product_id: selected.id }));
      setReceipt(null);
    }
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const options = useMemo(
    () => products.filter((p) => p.type === form.order_type && p.stock_status === "in_stock"),
    [products, form.order_type]
  );

  const chosen = products.find((p) => p.id === form.product_id);
  const total = chosen ? chosen.price_jmd * Number(form.quantity || 0) : 0;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onTypeChange = (v) => setForm((f) => ({ ...f, order_type: v, product_id: "" }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.phone || !form.address || !form.product_id) {
      toast.error("Please fill in your name, phone, address and select a size.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        customer_name: form.customer_name,
        phone: form.phone,
        email: form.email || null,
        address: form.address,
        items: [
          {
            product_id: chosen.id,
            product_name: chosen.name,
            size: chosen.size,
            order_type: chosen.type,
            quantity: Number(form.quantity),
            unit_price: chosen.price_jmd,
          },
        ],
        delivery_date: form.delivery_date,
        payment_method: form.payment_method,
        notes: form.notes,
        source: "online",
      });
      setReceipt({ order: data.order, bank: data.bank_details || bankDetails });
      toast.success("Order placed! 🚚");
      setForm(empty);
      document.getElementById("order-confirmation-receipt")?.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch (err) {
      toast.error("Could not place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <section id="order" className="relative py-24 cw-ocean-gradient overflow-hidden">
      <div className="absolute -top-10 left-1/3 w-96 h-96 bg-cyan-400 rounded-full cw-blob opacity-40" />
      <div className="max-w-5xl mx-auto px-5 lg:px-8 relative">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-300">Place Your Order</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-3">
            Fresh water, at your door.
          </h2>
          <p className="mt-4 text-sky-100/80">
            Fast doorstep delivery across Kingston, St. Andrew, and surrounding Jamaican parishes.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-5 gap-6">
          <motion.form
            data-testid="order-submission-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-3 bg-white rounded-3xl p-7 lg:p-9 shadow-2xl space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  data-testid="order-input-name"
                  value={form.customer_name}
                  onChange={(e) => set("customer_name", e.target.value)}
                  placeholder="e.g. Marcia Brown"
                  className="h-11 rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone / WhatsApp</Label>
                <Input
                  id="phone"
                  data-testid="order-input-phone"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="876-555-0100"
                  className="h-11 rounded-xl"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email <span className="text-slate-400 font-normal">(optional, for your receipt)</span></Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="order-input-email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="you@example.com"
                  className="h-11 rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="address">Delivery Address & Parish</Label>
                <Input
                  id="address"
                  data-testid="order-input-address"
                  value={form.address}
                  onChange={(e) => set("address", e.target.value)}
                  placeholder="Street, landmark, parish"
                  className="h-11 rounded-xl"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Order Type</Label>
                <Select value={form.order_type} onValueChange={onTypeChange}>
                  <SelectTrigger data-testid="order-select-type" className="h-11 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="purchase">New Purchase</SelectItem>
                    <SelectItem value="refill">Refill / Swap</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Bottle Size</Label>
                <Select value={form.product_id} onValueChange={(v) => set("product_id", v)}>
                  <SelectTrigger data-testid="order-select-size" className="h-11 rounded-xl">
                    <SelectValue placeholder="Select a size" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} — {formatJMD(p.price_jmd)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="qty">Quantity</Label>
                <Input
                  id="qty"
                  type="number"
                  min={1}
                  data-testid="order-input-quantity"
                  value={form.quantity}
                  onChange={(e) => set("quantity", e.target.value)}
                  className="h-11 rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="date">Preferred Delivery Date</Label>
                <Input
                  id="date"
                  type="date"
                  min={todayStr()}
                  data-testid="order-input-date"
                  value={form.delivery_date}
                  onChange={(e) => set("delivery_date", e.target.value)}
                  className="h-11 rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Payment Method</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { k: "cod", label: "Cash on Delivery", icon: Wallet, testid: "order-payment-cod" },
                  { k: "bank_transfer", label: "Bank Transfer", icon: Banknote, testid: "order-payment-bank-transfer" },
                ].map((pm) => (
                  <button
                    type="button"
                    key={pm.k}
                    data-testid={pm.testid}
                    onClick={() => set("payment_method", pm.k)}
                    className={`flex items-center gap-2 h-12 rounded-xl border-2 px-4 font-semibold text-sm transition-all ${
                      form.payment_method === pm.k
                        ? "border-sky-500 bg-sky-50 text-sky-700"
                        : "border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}
                  >
                    <pm.icon className="w-4 h-4" /> {pm.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                data-testid="order-input-notes"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                placeholder="Delivery instructions, landmarks…"
                className="rounded-xl resize-none"
                rows={2}
              />
            </div>

            <Button
              type="submit"
              data-testid="order-submit-button"
              disabled={submitting}
              className="w-full h-13 py-6 rounded-xl text-base font-bold bg-sky-600 hover:bg-sky-500 text-white"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Place Order · {formatJMD(total)}</>}
            </Button>
          </motion.form>

          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white/10 cw-glass-dark rounded-3xl p-6 border border-sky-300/20">
              <div className="flex items-center gap-2 text-sky-200 font-semibold text-sm uppercase tracking-wider">
                <Package className="w-4 h-4" /> Order Summary
              </div>
              <div className="mt-4 space-y-3 text-sky-100/90 text-sm">
                <div className="flex justify-between"><span>Item</span><span className="font-semibold text-white text-right">{chosen ? chosen.name : "—"}</span></div>
                <div className="flex justify-between"><span>Unit price</span><span className="font-mono-cw">{chosen ? formatJMD(chosen.price_jmd) : "—"}</span></div>
                <div className="flex justify-between"><span>Quantity</span><span>{form.quantity}</span></div>
                <div className="flex items-center justify-between gap-2"><span><Calendar className="w-3.5 h-3.5 inline mr-1" />Delivery</span><span>{form.delivery_date}</span></div>
                <div className="border-t border-sky-300/20 pt-3 flex justify-between items-center">
                  <span className="font-semibold text-white">Total</span>
                  <span className="font-mono-cw text-2xl font-extrabold text-sky-300">{formatJMD(total)}</span>
                </div>
              </div>
            </div>
            <div className="bg-white/5 rounded-3xl p-6 border border-sky-300/10 text-sky-100/70 text-sm leading-relaxed">
              <strong className="text-white">Payment:</strong> {PAYMENT_LABEL[form.payment_method]}.{" "}
              {form.payment_method === "bank_transfer"
                ? "Full bank details appear on your receipt after you place the order, scroll down to see it."
                : "Pay our driver in cash when your water arrives."}
            </div>
          </div>
        </div>

        {receipt && (
          <motion.div
            id="order-confirmation-receipt"
            data-testid="order-confirmation-receipt"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-10 bg-white rounded-3xl p-8 shadow-2xl"
          >
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-10 h-10 text-emerald-500" />
              <div>
                <h3 className="font-display text-2xl font-bold text-slate-900">Order Confirmed!</h3>
                <p className="text-slate-500 text-sm">Your order ID is <span className="font-mono-cw font-bold text-sky-700">{receipt.order.order_code}</span></p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mt-6">
              <div className="bg-slate-50 rounded-2xl p-5 text-sm space-y-2">
                <div className="flex justify-between"><span className="text-slate-500">Customer</span><span className="font-semibold text-slate-800">{receipt.order.customer_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Track it</span><Link to={`/track?code=${receipt.order.order_code}`} data-testid="receipt-track-link" className="font-semibold text-sky-700 underline">Track this order</Link></div>
                <div className="flex justify-between"><span className="text-slate-500">Delivery date</span><span className="font-semibold text-slate-800">{receipt.order.delivery_date}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Payment</span><span className="font-semibold text-slate-800">{PAYMENT_LABEL[receipt.order.payment_method]}</span></div>
                <div className="flex justify-between border-t pt-2"><span className="text-slate-500">Total</span><span className="font-mono-cw font-extrabold text-sky-700">{formatJMD(receipt.order.total_amount)}</span></div>
              </div>

              {receipt.order.payment_method === "bank_transfer" && receipt.bank && (
                <div data-testid="bank-transfer-details-card" className="rounded-2xl p-5 border-2 border-sky-200 bg-sky-50/60">
                  <div className="flex items-center gap-2 text-sky-800 font-bold text-sm uppercase tracking-wider mb-3">
                    <Banknote className="w-4 h-4" /> Bank Transfer Details
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="text-slate-700"><span className="text-slate-400 text-xs block">Bank</span>{receipt.bank.bank_name}</div>
                    <div className="flex items-center justify-between">
                      <div className="text-slate-700"><span className="text-slate-400 text-xs block">Account Name</span>{receipt.bank.account_name}</div>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => copy(receipt.bank.account_name, "Account name")}><Copy className="w-4 h-4" /></Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-slate-700 font-mono-cw"><span className="text-slate-400 text-xs block font-sans">Account Number</span>{receipt.bank.account_number}</div>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => copy(receipt.bank.account_number, "Account number")}><Copy className="w-4 h-4" /></Button>
                    </div>
                    <div className="text-slate-700"><span className="text-slate-400 text-xs block">Branch</span>{receipt.bank.branch}</div>
                    <p className="text-xs text-slate-500 border-t border-sky-200 pt-2 mt-2">{receipt.bank.instructions}</p>
                  </div>
                </div>
              )}

              {receipt.order.payment_method === "cod" && (
                <div className="rounded-2xl p-5 border-2 border-emerald-200 bg-emerald-50/60 flex items-center gap-3">
                  <Wallet className="w-8 h-8 text-emerald-600" />
                  <div className="text-sm text-emerald-800">
                    <strong className="block">Cash on Delivery</strong>
                    Have {formatJMD(receipt.order.total_amount)} ready for our driver on {receipt.order.delivery_date}.
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};
